import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Slider } from "../components/ui/slider";
import { useCart } from "../context/CartContext";
import { Upload, ZoomIn, ZoomOut, RotateCw, ShoppingCart, Plus, Trash2, Check, Truck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

// Default shipping options (fallback)
const DEFAULT_SHIPPING_OPTIONS = [
  { id: 'standard', name: 'Standard Shipping', price: 5.99, days: '5-7 business days' },
  { id: 'express', name: 'Express Shipping', price: 12.99, days: '2-3 business days' },
  { id: 'overnight', name: 'Overnight Shipping', price: 24.99, days: '1 business day' },
  { id: 'pickup', name: 'Local Pickup', price: 0, days: 'Pick up in Kailua-Kona' }
];

export default function OreoCookieProduct() {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  // Image state for up to 4 cookies
  const [images, setImages] = useState([null, null, null, null]);
  const [previews, setPreviews] = useState([null, null, null, null]);
  const [adjustments, setAdjustments] = useState([
    { scale: 1, offsetX: 0, offsetY: 0, rotation: 0 },
    { scale: 1, offsetX: 0, offsetY: 0, rotation: 0 },
    { scale: 1, offsetX: 0, offsetY: 0, rotation: 0 },
    { scale: 1, offsetX: 0, offsetY: 0, rotation: 0 }
  ]);
  const [selectedSlot, setSelectedSlot] = useState(0);
  const [useSameImage, setUseSameImage] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  
  // Shipping state
  const [shippingOptions, setShippingOptions] = useState(DEFAULT_SHIPPING_OPTIONS);
  const [selectedShipping, setSelectedShipping] = useState(DEFAULT_SHIPPING_OPTIONS[0]);
  const [hasFreeShipping, setHasFreeShipping] = useState(false);
  
  const canvasRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const imageRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const fileInputRef = useRef(null);
  
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Oreo cookie dimensions (1.5" diameter, displayed at 130px)
  const COOKIE_SIZE = 130;
  const COOKIE_SIZE_INCHES = 1.5;
  
  // Pricing
  const BASE_PRICE = 16;
  const DIFFERENT_IMAGES_EXTRA = 2;
  
  // Fetch shipping options on mount
  useEffect(() => {
    const fetchShippingOptions = async () => {
      try {
        const response = await fetch(`${API}/shipping-options`);
        if (response.ok) {
          const data = await response.json();
          if (data.shipping_options && data.shipping_options.length > 0) {
            setShippingOptions(data.shipping_options);
            setSelectedShipping(data.shipping_options[0]);
          }
          // Check if this product has free shipping (using product ID or type)
          // For now, we'll use a placeholder - you can add the actual product ID check
          if (data.free_shipping_products && data.free_shipping_products.includes('oreo-cookie')) {
            setHasFreeShipping(true);
          }
        }
      } catch (error) {
        console.log('Using default shipping options');
      }
    };
    fetchShippingOptions();
  }, []);
  
  const getPrice = () => {
    if (useSameImage) return BASE_PRICE;
    return BASE_PRICE + DIFFERENT_IMAGES_EXTRA;
  };
  
  const getShippingPrice = () => {
    if (hasFreeShipping && selectedShipping.id === 'standard') {
      return 0;
    }
    return selectedShipping.price;
  };
  
  const getTotalPrice = () => {
    return getPrice() + getShippingPrice();
  };
  
  // Load image when file selected
  const handleFileChange = (e, slotIndex) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const newPreviews = [...previews];
      const newImages = [...images];
      
      if (useSameImage) {
        // Apply same image to all slots
        newPreviews.fill(reader.result);
        newImages.fill(file);
        setPreviews(newPreviews);
        setImages(newImages);
        
        // Load image for all canvases
        const img = new Image();
        img.onload = () => {
          imageRefs.forEach(ref => { ref.current = img; });
          drawAllCanvases();
        };
        img.src = reader.result;
      } else {
        // Apply to specific slot
        newPreviews[slotIndex] = reader.result;
        newImages[slotIndex] = file;
        setPreviews(newPreviews);
        setImages(newImages);
        
        const img = new Image();
        img.onload = () => {
          imageRefs[slotIndex].current = img;
          drawCanvas(slotIndex);
        };
        img.src = reader.result;
      }
    };
    reader.readAsDataURL(file);
  };
  
  // Draw canvas for a specific slot
  const drawCanvas = (slotIndex) => {
    const canvas = canvasRefs[slotIndex].current;
    const img = imageRefs[slotIndex].current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = COOKIE_SIZE;
    canvas.height = COOKIE_SIZE;
    
    // Clear canvas
    ctx.fillStyle = '#f5f5dc'; // Cookie color background
    ctx.beginPath();
    ctx.arc(COOKIE_SIZE/2, COOKIE_SIZE/2, COOKIE_SIZE/2, 0, Math.PI * 2);
    ctx.fill();
    
    if (!img) {
      // Draw placeholder
      ctx.fillStyle = '#d4a574';
      ctx.beginPath();
      ctx.arc(COOKIE_SIZE/2, COOKIE_SIZE/2, COOKIE_SIZE/2 - 5, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = '#8b4513';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Click to add', COOKIE_SIZE/2, COOKIE_SIZE/2 - 5);
      ctx.fillText('your image', COOKIE_SIZE/2, COOKIE_SIZE/2 + 10);
      return;
    }
    
    // Save for clipping
    ctx.save();
    ctx.beginPath();
    ctx.arc(COOKIE_SIZE/2, COOKIE_SIZE/2, COOKIE_SIZE/2 - 3, 0, Math.PI * 2);
    ctx.clip();
    
    // White background for image area
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, COOKIE_SIZE, COOKIE_SIZE);
    
    const adj = adjustments[slotIndex];
    let imgWidth = img.width;
    let imgHeight = img.height;
    
    // Handle rotation
    if (adj.rotation === 90 || adj.rotation === 270) {
      [imgWidth, imgHeight] = [imgHeight, imgWidth];
    }
    
    // Calculate scale
    const baseScale = Math.min(COOKIE_SIZE / imgWidth, COOKIE_SIZE / imgHeight);
    const finalScale = baseScale * adj.scale;
    
    const drawWidth = imgWidth * finalScale;
    const drawHeight = imgHeight * finalScale;
    
    const drawX = (COOKIE_SIZE - drawWidth) / 2 + (adj.offsetX / 100) * COOKIE_SIZE;
    const drawY = (COOKIE_SIZE - drawHeight) / 2 + (adj.offsetY / 100) * COOKIE_SIZE;
    
    // Apply rotation
    ctx.translate(COOKIE_SIZE / 2, COOKIE_SIZE / 2);
    ctx.rotate((adj.rotation * Math.PI) / 180);
    ctx.translate(-COOKIE_SIZE / 2, -COOKIE_SIZE / 2);
    
    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    ctx.restore();
    
    // Draw cookie edge/border
    ctx.strokeStyle = '#8b4513';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(COOKIE_SIZE/2, COOKIE_SIZE/2, COOKIE_SIZE/2 - 2, 0, Math.PI * 2);
    ctx.stroke();
  };
  
  const drawAllCanvases = () => {
    [0, 1, 2, 3].forEach(i => drawCanvas(i));
  };
  
  // Redraw when adjustments change
  useEffect(() => {
    drawAllCanvases();
  }, [adjustments]);
  
  // Update adjustment for selected slot
  const updateAdjustment = (field, value) => {
    const newAdjustments = [...adjustments];
    if (useSameImage) {
      // Apply to all
      newAdjustments.forEach(adj => { adj[field] = value; });
    } else {
      newAdjustments[selectedSlot][field] = value;
    }
    setAdjustments(newAdjustments);
  };
  
  // Handle drag for panning
  const handleMouseDown = (e, slotIndex) => {
    if (!imageRefs[slotIndex].current) {
      // No image - open file picker
      fileInputRef.current.click();
      setSelectedSlot(slotIndex);
      return;
    }
    setIsDragging(true);
    setSelectedSlot(slotIndex);
    const rect = canvasRefs[slotIndex].current.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    setDragStart({ x, y, startOffsetX: adjustments[slotIndex].offsetX, startOffsetY: adjustments[slotIndex].offsetY });
  };
  
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const rect = canvasRefs[selectedSlot].current.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const deltaX = ((x - dragStart.x) / COOKIE_SIZE) * 100;
    const deltaY = ((y - dragStart.y) / COOKIE_SIZE) * 100;
    
    updateAdjustment('offsetX', dragStart.startOffsetX + deltaX);
    updateAdjustment('offsetY', dragStart.startOffsetY + deltaY);
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // Remove image from slot
  const removeImage = (slotIndex) => {
    const newPreviews = [...previews];
    const newImages = [...images];
    newPreviews[slotIndex] = null;
    newImages[slotIndex] = null;
    setPreviews(newPreviews);
    setImages(newImages);
    imageRefs[slotIndex].current = null;
    drawCanvas(slotIndex);
  };
  
  // Toggle same/different images
  const toggleImageMode = (same) => {
    setUseSameImage(same);
    if (same && previews[0]) {
      // Copy first image to all slots
      const newPreviews = previews.map(() => previews[0]);
      const newImages = images.map(() => images[0]);
      setPreviews(newPreviews);
      setImages(newImages);
      imageRefs.forEach(ref => { ref.current = imageRefs[0].current; });
      
      // Copy first adjustment to all
      const newAdjustments = adjustments.map(() => ({ ...adjustments[0] }));
      setAdjustments(newAdjustments);
    }
  };
  
  // Add to cart
  const handleAddToCart = async () => {
    // Check if at least first image is uploaded
    if (!images[0]) {
      alert("Please upload at least one image");
      return;
    }
    
    // For different images mode, check all slots
    if (!useSameImage) {
      const emptySlots = images.filter(img => !img).length;
      if (emptySlots > 0) {
        const proceed = window.confirm(`${emptySlots} cookie(s) have no image. Continue anyway?`);
        if (!proceed) return;
      }
    }
    
    setAddingToCart(true);
    
    try {
      // Convert canvas images to base64 for storage
      const cookieImages = [];
      for (let i = 0; i < 4; i++) {
        if (canvasRefs[i].current && (useSameImage ? images[0] : images[i])) {
          cookieImages.push({
            index: i,
            image: canvasRefs[i].current.toDataURL('image/png'),
            adjustment: adjustments[i]
          });
        }
      }
      
      // Create cart item
      const cartItem = {
        id: `oreo-cookie-${Date.now()}`,
        name: "Custom Oreo Cookie Box",
        price: getPrice(),
        quantity: 1,
        image: cookieImages[0]?.image || "/placeholder-cookie.png",
        customization: {
          type: "oreo-cookie",
          useSameImage,
          cookieImages,
          cookieCount: 4
        },
        shipping: {
          method: selectedShipping.id,
          name: selectedShipping.name,
          price: selectedShipping.price,
          days: selectedShipping.days
        }
      };
      
      addToCart(cartItem);
      
      // Navigate to cart
      navigate("/edible-greetings/cart");
    } catch (error) {
      console.error("Error adding to cart:", error);
      alert("Failed to add to cart. Please try again.");
    } finally {
      setAddingToCart(false);
    }
  };
  
  const hasAnyImage = images.some(img => img !== null);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 py-8 px-4"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchMove={handleMouseMove}
      onTouchEnd={handleMouseUp}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-amber-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            🍪 Custom Oreo Cookies
          </h1>
          <p className="text-amber-700 text-lg">
            Your photo printed on delicious Oreo cookies!
          </p>
        </div>
        
        {/* Product Info */}
        <Card className="mb-6 bg-white/80 backdrop-blur shadow-xl border-2 border-amber-200">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-amber-900">Box of 4 Cookies</h2>
                <p className="text-amber-600 mt-1">1.5&quot; diameter each • Edible ink printing</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-amber-900">${getPrice()}</div>
                {!useSameImage && (
                  <p className="text-sm text-amber-600">Includes $2 for different images</p>
                )}
              </div>
            </div>
            
            {/* Same/Different toggle */}
            <div className="mt-4 flex gap-3">
              <Button
                variant={useSameImage ? "default" : "outline"}
                onClick={() => toggleImageMode(true)}
                className={useSameImage ? "bg-amber-600 hover:bg-amber-700" : ""}
              >
                <Check className={`w-4 h-4 mr-2 ${useSameImage ? '' : 'opacity-0'}`} />
                Same Image (All 4)
              </Button>
              <Button
                variant={!useSameImage ? "default" : "outline"}
                onClick={() => toggleImageMode(false)}
                className={!useSameImage ? "bg-amber-600 hover:bg-amber-700" : ""}
              >
                <Check className={`w-4 h-4 mr-2 ${!useSameImage ? '' : 'opacity-0'}`} />
                Different Images (+$2)
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Cookie Grid */}
        <Card className="mb-6 bg-white/80 backdrop-blur shadow-xl border-2 border-amber-200">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-amber-900 mb-4">
              {useSameImage ? "Your Cookie Design" : "Design Each Cookie"}
            </h3>
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, selectedSlot)}
              className="hidden"
            />
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 justify-items-center">
              {[0, 1, 2, 3].map((index) => (
                <div key={index} className="relative">
                  <div 
                    className={`relative cursor-pointer transition-all ${
                      selectedSlot === index && !useSameImage ? 'ring-4 ring-amber-400 rounded-full' : ''
                    }`}
                    onClick={() => !useSameImage && setSelectedSlot(index)}
                  >
                    <canvas
                      ref={canvasRefs[index]}
                      width={COOKIE_SIZE}
                      height={COOKIE_SIZE}
                      className="rounded-full shadow-lg cursor-move"
                      style={{ touchAction: 'none' }}
                      onMouseDown={(e) => handleMouseDown(e, index)}
                      onTouchStart={(e) => handleMouseDown(e, index)}
                    />
                    {(useSameImage ? previews[0] : previews[index]) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage(useSameImage ? 0 : index);
                          if (useSameImage) {
                            setPreviews([null, null, null, null]);
                            setImages([null, null, null, null]);
                            imageRefs.forEach(ref => { ref.current = null; });
                            drawAllCanvases();
                          }
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <p className="text-center text-sm text-amber-700 mt-2">Cookie {index + 1}</p>
                </div>
              ))}
            </div>
            
            {/* Adjustment Controls */}
            {hasAnyImage && (
              <div className="mt-6 bg-amber-50 rounded-xl p-4">
                <p className="text-sm text-amber-700 mb-3">
                  {useSameImage ? "Adjust your image:" : `Adjust Cookie ${selectedSlot + 1}:`}
                </p>
                
                {/* Zoom Slider */}
                <div className="flex items-center gap-3 mb-3">
                  <ZoomOut className="w-5 h-5 text-amber-600" />
                  <Slider
                    value={[adjustments[useSameImage ? 0 : selectedSlot].scale]}
                    onValueChange={(v) => updateAdjustment('scale', v[0])}
                    min={0.5}
                    max={3}
                    step={0.05}
                    className="flex-1"
                  />
                  <ZoomIn className="w-5 h-5 text-amber-600" />
                  <span className="text-sm text-amber-700 w-16 text-right">
                    {Math.round(adjustments[useSameImage ? 0 : selectedSlot].scale * 100)}%
                  </span>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateAdjustment('rotation', (adjustments[useSameImage ? 0 : selectedSlot].rotation + 90) % 360)}
                    className="flex-1"
                  >
                    <RotateCw className="w-4 h-4 mr-1" />
                    Rotate
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newAdj = { scale: 1, offsetX: 0, offsetY: 0, rotation: 0 };
                      if (useSameImage) {
                        setAdjustments([newAdj, { ...newAdj }, { ...newAdj }, { ...newAdj }]);
                      } else {
                        const newAdjustments = [...adjustments];
                        newAdjustments[selectedSlot] = newAdj;
                        setAdjustments(newAdjustments);
                      }
                    }}
                    className="flex-1"
                  >
                    Reset
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current.click()}
                    className="flex-1"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Change
                  </Button>
                </div>
              </div>
            )}
            
            {!hasAnyImage && (
              <div className="mt-6 text-center">
                <Button
                  onClick={() => fileInputRef.current.click()}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg"
                >
                  <Upload className="w-5 h-5 mr-2" />
                  Upload Your Image
                </Button>
                <p className="text-sm text-amber-600 mt-2">Click a cookie or this button to add your image</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Shipping Options */}
        <Card className="mb-6 bg-white/80 backdrop-blur shadow-xl border-2 border-amber-200">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Select Shipping Method
              {hasFreeShipping && (
                <span className="ml-2 text-xs bg-green-500 text-white px-2 py-1 rounded-full">
                  Free Standard Shipping!
                </span>
              )}
            </h3>
            
            <div className="grid gap-3">
              {shippingOptions.map((option) => {
                const isFreeForThisProduct = hasFreeShipping && option.id === 'standard';
                const displayPrice = isFreeForThisProduct ? 0 : option.price;
                
                return (
                  <div
                    key={option.id}
                    onClick={() => setSelectedShipping(option)}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedShipping.id === option.id 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedShipping.id === option.id ? 'border-amber-500 bg-amber-500' : 'border-gray-300'
                      }`}>
                        {selectedShipping.id === option.id && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {option.name}
                          {isFreeForThisProduct && (
                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                              FREE for this product
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">{option.days}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {isFreeForThisProduct ? (
                        <div>
                          <p className="font-semibold text-green-600">FREE</p>
                          <p className="text-xs text-gray-400 line-through">${option.price.toFixed(2)}</p>
                        </div>
                      ) : (
                        <p className="font-semibold text-amber-900">
                          {displayPrice === 0 ? 'FREE' : `$${displayPrice.toFixed(2)}`}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        {/* Order Summary */}
        <Card className="mb-6 bg-white/80 backdrop-blur shadow-xl border-2 border-amber-200">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-amber-900 mb-4">Order Summary</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between text-gray-700">
                <span>Custom Oreo Cookie Box (4 cookies)</span>
                <span>${getPrice().toFixed(2)}</span>
              </div>
              {!useSameImage && (
                <div className="flex justify-between text-gray-500 text-sm">
                  <span className="ml-4">Different images fee</span>
                  <span>+$2.00</span>
                </div>
              )}
              <div className="flex justify-between text-gray-700">
                <span>{selectedShipping.name}</span>
                {hasFreeShipping && selectedShipping.id === 'standard' ? (
                  <span className="text-green-600 font-medium">FREE</span>
                ) : (
                  <span>{getShippingPrice() === 0 ? 'FREE' : `$${getShippingPrice().toFixed(2)}`}</span>
                )}
              </div>
              <div className="border-t border-amber-200 pt-3 flex justify-between font-bold text-lg text-amber-900">
                <span>Total</span>
                <span>${getTotalPrice().toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Add to Cart Button */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/edible-greetings/catalog")}
            className="flex-1 py-6"
          >
            Continue Shopping
          </Button>
          <Button
            onClick={handleAddToCart}
            disabled={!hasAnyImage || addingToCart}
            className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white py-6 text-lg font-semibold shadow-lg"
          >
            {addingToCart ? (
              <>
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
                Adding...
              </>
            ) : (
              <>
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart - ${getTotalPrice().toFixed(2)}
              </>
            )}
          </Button>
        </div>
        
        {/* Product Details */}
        <Card className="mt-6 bg-white/80 backdrop-blur border-2 border-amber-200">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-amber-900 mb-3">Product Details</h3>
            <ul className="space-y-2 text-amber-700">
              <li>• Box contains 4 custom printed Oreo cookies</li>
              <li>• Each cookie is 1.5&quot; (38mm) diameter</li>
              <li>• Food-safe edible ink printing</li>
              <li>• Perfect for parties, gifts, and special occasions</li>
              <li>• Ships within 3-5 business days</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
